import {
  Container,
  Box,
  Header,
  Button,
  Grid,
  SpaceBetween,
  ExpandableSection,
  FormField,
  Input,
  Select,
  Table,
  Link,
  StatusIndicator,
  TextFilter,
  Checkbox,
  DatePicker,
  Pagination,
  Alert,
} from '@cloudscape-design/components';
import type { SelectProps, TableProps } from '@cloudscape-design/components';
import { ViewLayout } from '../../components/ViewLayout';
import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  createDistributorReport,
  ExistingClientError,
  getDistributorReports,
} from '../../common/api/distributorDashboard';
import type {
  CreateDistributorReportResponse,
  DistributorReport,
  DistributorReportsQuery,
} from '../../common/api/distributorDashboard';
import { LoadingOrError } from '../../components/Loading';
import MultiAutoCompleteSearch from '../../components/MultiAutoCompleteSearch';
import type { Suggestion } from '../../common/useMaps';
import { BUSINESS_TYPES } from '../../common/types';
import { getBusinessType } from '../../common/businessTypes';
import { useDistributorAuth } from '../../auth/useDistributorAuth';
import { ReportProgress } from '../home';
import { DistributorTopNavigation } from '../../common/navigation';
import ReportCardHomeHeader from '../../components/ReportCardHomeHeader';
import { StarsIcon } from '../../components/StarsIcon';
import { formatDateStringWithTime } from '../../common/formatting/dateFormat';
import { computeHasGooglePeriods, computeStatus } from '../../common/cardLogic';
import { DistributorHierarchy } from '../../components/distributors/DistributorHierarchy';

const PAGE_SIZE = 10;

function localDateBoundary(value: string, endOfDay = false): string | undefined {
  if (!value) return undefined;
  return new Date(`${value}T${endOfDay ? '23:59:59.999' : '00:00:00.000'}`).toISOString();
}

function useCompactHistory(): boolean {
  const [compact, setCompact] = useState(() => window.innerWidth < 720);
  useEffect(() => {
    const update = () => setCompact(window.innerWidth < 720);
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return compact;
}

function ScorecardState({ report }: { report: DistributorReport }) {
  const status = computeStatus(report.tasks, computeHasGooglePeriods(report.periods));
  if (status === 'success') {
    return <StatusIndicator type="success">Completed</StatusIndicator>;
  }
  if (status === 'error') {
    return <StatusIndicator type="error">Failed</StatusIndicator>;
  }
  return <StatusIndicator type="in-progress">In Progress</StatusIndicator>;
}

function reportTypeLabel(reportType: string | null | undefined): string {
  if (reportType === 'distributor_internal') return 'Internal';
  if (reportType === 'distributor') return 'Public';
  return reportType ?? 'Historical';
}

function BusinessCell({ report }: { report: DistributorReport }) {
  return (
    <SpaceBetween direction="vertical" size="xxxs">
      <Button
        href={`/${report.slug ?? report.uuid}`}
        variant="inline-link"
        target="_blank"
        iconName="transcript"
        wrapText
        iconAlign="left"
      >
        {report.place?.name ?? 'Business unavailable'}
      </Button>
      {report.place?.address && (
        <Box variant="span" color="text-body-secondary">
          {report.place.address}
        </Box>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <span>{report.place?.rating?.toFixed(1) ?? '?.?'}</span>
        <StarsIcon stars={report.place?.rating ?? undefined} />
        {report.place?.cid_url ? (
          <Link href={report.place.cid_url} target="_blank" variant="secondary">
            {report.place.total_review_count?.toLocaleString() ?? '?'} reviews
          </Link>
        ) : (
          <Box variant="span" color="text-body-secondary">
            {report.place?.total_review_count?.toLocaleString() ?? '?'} reviews
          </Box>
        )}
      </div>
    </SpaceBetween>
  );
}

function ScorecardCell({ report }: { report: DistributorReport }) {
  return (
    <SpaceBetween direction="vertical" size="xxxs">
      <ScorecardState report={report} />
      <Box variant="span">{formatDateStringWithTime(report.report_at)}</Box>
      <Box variant="span" color="text-body-secondary">
        {[reportTypeLabel(report.report_type), report.business_type].filter(Boolean).join(' · ')}
      </Box>
    </SpaceBetween>
  );
}

function OwnerCell({ report }: { report: DistributorReport }) {
  const owner = report.distributor;
  const ownerName = owner
    ? owner.company || `${owner.first_name} ${owner.last_name}`.trim()
    : 'Owner unavailable';
  return (
    <SpaceBetween direction="vertical" size="xxxs">
      <Box variant="span">{ownerName}</Box>
      {owner && (
        <Link href={`/d/${owner.path}`} target="_blank" variant="secondary">
          /d/{owner.path}
        </Link>
      )}
      {report.runner ? (
        <SpaceBetween direction="vertical" size="xxxs">
          <Box variant="span" color="text-body-secondary">
            Run by {report.runner.name || report.runner.email}
          </Box>
          {report.runner.name !== report.runner.email && (
            <div className="break-all">
              <Box variant="span" color="text-body-secondary">
                {report.runner.email}
              </Box>
            </div>
          )}
        </SpaceBetween>
      ) : (
        <Box variant="span" color="text-body-secondary">
          Individual unavailable
        </Box>
      )}
    </SpaceBetween>
  );
}

export default function DistributorDashboardView() {
  const { user, logout } = useDistributorAuth();
  const [selectedItems, setSelectedItems] = useState<Suggestion[]>([]);
  const [createdReports, setCreatedReports] = useState<CreateDistributorReportResponse[]>([]);
  const [blockedBusinesses, setBlockedBusinesses] = useState<string[]>([]);
  const [createdDelivery, setCreatedDelivery] = useState<'text-email' | 'text' | 'email'>();
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [businessTypeFilter, setBusinessTypeFilter] = useState<SelectProps.Option | null>(null);
  const [reportTypeFilter, setReportTypeFilter] = useState<SelectProps.Option | null>(null);
  const [reportAtFrom, setReportAtFrom] = useState('');
  const [reportAtTo, setReportAtTo] = useState('');
  const [sortOption, setSortOption] = useState<SelectProps.Option>({
    label: 'Newest first',
    value: 'report_at:desc',
  });
  const [page, setPage] = useState(1);
  const compactHistory = useCompactHistory();

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearch(searchText.trim()), 350);
    return () => window.clearTimeout(timeout);
  }, [searchText]);

  // Optional contact fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [textScorecard, setTextScorecard] = useState(true);
  const [showErrors, setShowErrors] = useState(false);
  const [title, setTitle] = useState('');
  const [businessType, setBusinessType] = useState<SelectProps.Option | null>(null);

  const reportsQuery = useQuery({
    queryKey: [
      'distributor-reports',
      debouncedSearch,
      ownerFilter,
      businessTypeFilter?.value,
      reportTypeFilter?.value,
      reportAtFrom,
      reportAtTo,
      sortOption.value,
      page,
    ],
    queryFn: () => {
      const [sortBy, sortDirection] = (sortOption.value ?? 'report_at:desc').split(':');
      const query = {
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        search: debouncedSearch || undefined,
        owner_distributor: ownerFilter.trim() || undefined,
        business_type: businessTypeFilter?.value || undefined,
        report_type: reportTypeFilter?.value || undefined,
        report_at_from: localDateBoundary(reportAtFrom),
        report_at_to: localDateBoundary(reportAtTo, true),
        sort_by: sortBy as DistributorReportsQuery['sort_by'],
        sort_direction: sortDirection as DistributorReportsQuery['sort_direction'],
      } satisfies DistributorReportsQuery;
      return getDistributorReports(query);
    },
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const results: CreateDistributorReportResponse[] = [];
      const blocked: string[] = [];
      for (const item of selectedItems) {
        const hasEmail = Boolean(email.trim());
        const hasTextNumber = textScorecard && Boolean(phoneNumber.trim());
        const contact = hasEmail || hasTextNumber
          ? {
              first_name: firstName.trim() || undefined,
              last_name: lastName.trim() || undefined,
              email: hasEmail ? email.trim() : undefined,
              phone_number: hasTextNumber ? phoneNumber.trim() : undefined,
              title: title.trim() || undefined,
            }
          : undefined;
        try {
          const result = await createDistributorReport({
            place_id: item.placeId,
            contact,
            business_type:
              businessType?.value === 'Other' ? undefined : (businessType?.value ?? undefined),
          });
          if (result) {
            results.push(result);
            setCreatedReports([...results]);
          }
        } catch (error) {
          if (!(error instanceof ExistingClientError)) throw error;
          blocked.push(item.label);
          setBlockedBusinesses([...blocked]);
        }
      }
      return results;
    },
    onSuccess: (data) => {
      setSelectedItems([]);
      setCreatedReports(data);
      const hasEmail = Boolean(email.trim());
      const hasTextNumber = textScorecard && Boolean(phoneNumber.trim());
      setCreatedDelivery(
        hasEmail && hasTextNumber
          ? 'text-email'
          : hasTextNumber
            ? 'text'
            : hasEmail
              ? 'email'
              : undefined,
      );
      reportsQuery.refetch();
    },
  });

  useEffect(() => {
    if (selectedItems.length > 0) {
      const googleTags = selectedItems[0].tags;
      const bt = getBusinessType(googleTags);
      setBusinessType(bt ? { label: bt, value: bt } : { label: 'Other', value: 'Other' });
    }
  }, [selectedItems]);

  const handleCreate = () => {
    if (createMutation.isPending || selectedItems.length === 0) return;
    const validEmail = !email.trim() || email.trim().includes('@');
    const validPhone =
      !textScorecard ||
      !phoneNumber.trim() ||
      (phoneNumber.trim().length >= 10 && phoneNumber.trim().length <= 15);
    if (!validEmail || !validPhone) {
      setShowErrors(true);
      return;
    }
    setCreatedReports([]);
    setBlockedBusinesses([]);
    createMutation.mutate();
  };

  const reportItems = reportsQuery.data?.items ?? [];
  const total = reportsQuery.data?.total ?? 0;
  const pagesCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasFilters = Boolean(
    searchText || ownerFilter || businessTypeFilter || reportTypeFilter || reportAtFrom || reportAtTo,
  );
  const resetPage = () => setPage(1);
  const desktopColumns: TableProps.ColumnDefinition<DistributorReport>[] = [
    {
      id: 'business',
      header: 'Business',
      cell: (item) => <BusinessCell report={item} />,
    },
    {
      id: 'scorecard',
      header: 'Scorecard',
      cell: (item) => <ScorecardCell report={item} />,
    },
    {
      id: 'owner',
      header: 'Owner and runner',
      cell: (item) => <OwnerCell report={item} />,
    },
  ];
  const compactColumns: TableProps.ColumnDefinition<DistributorReport>[] = [
    {
      id: 'history',
      header: 'Scorecard history',
      cell: (item) => (
        <SpaceBetween direction="vertical" size="s">
          <BusinessCell report={item} />
          <ScorecardCell report={item} />
          <OwnerCell report={item} />
        </SpaceBetween>
      ),
    },
  ];

  return (
    <>
      <DistributorTopNavigation email={user?.email} onLogout={logout} />
      <ViewLayout
        header={<ReportCardHomeHeader logoHref="https://manage.realtimefeedback.com" />}
        contentType="dashboard"
        navigationHide
        headerVariant="high-contrast"
      >
        <Grid
          gridDefinition={Array.from(
            { length: createdReports.length > 0 ? 4 : 3 },
            () => ({ colspan: { default: 12 } }),
          )}
        >
          {/* Create Scorecard */}
          <Container
            header={
              <Header
                variant="h2"
                description="Search for a business to create a Reputation Scorecard"
              >
                Create a Scorecard
              </Header>
            }
          >
            <SpaceBetween direction="vertical" size="s">
              <MultiAutoCompleteSearch
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
                loading={createMutation.isPending}
              />
              <Grid gridDefinition={[{ colspan: { default: 12, xs: 6 } }]}>
                <FormField label="Business Type">
                  <Select
                    selectedOption={businessType}
                    options={BUSINESS_TYPES}
                    onChange={({ detail }) => setBusinessType(detail.selectedOption)}
                    disabled={createMutation.isPending}
                    placeholder="Select a business type"
                  />
                </FormField>
              </Grid>
              <ExpandableSection headerText="Scorecard delivery (optional)">
                <Grid
                  gridDefinition={[
                    { colspan: { default: 12, xs: 6 } },
                    { colspan: { default: 12, xs: 6 } },
                    { colspan: { default: 12, xs: 6 } },
                    { colspan: { default: 12, xs: 6 } },
                    { colspan: { default: 12, xs: 6 } },
                    { colspan: { default: 12, xs: 6 } },
                  ]}
                >
                  <FormField label="First Name">
                    <Input
                      value={firstName}
                      onChange={({ detail }) => setFirstName(detail.value)}
                      disabled={createMutation.isPending}
                    />
                  </FormField>
                  <FormField label="Last Name">
                    <Input
                      value={lastName}
                      onChange={({ detail }) => setLastName(detail.value)}
                      placeholder="(optional)"
                      disabled={createMutation.isPending}
                    />
                  </FormField>
                  <FormField
                    label="Email"
                    errorText={
                      showErrors && email.trim() && !email.trim().includes('@')
                        ? 'Enter a valid email address'
                        : undefined
                    }
                  >
                    <Input
                      value={email}
                      onChange={({ detail }) => setEmail(detail.value)}
                      placeholder="(optional)"
                      disabled={createMutation.isPending}
                    />
                  </FormField>
                  <FormField
                    label="Phone Number"
                    errorText={
                      showErrors &&
                      textScorecard &&
                      phoneNumber.trim() &&
                      (phoneNumber.trim().length < 10 || phoneNumber.trim().length > 15)
                        ? 'Enter a valid phone number'
                        : undefined
                    }
                  >
                    <Input
                      value={phoneNumber}
                      onChange={({ detail }) => setPhoneNumber(detail.value)}
                      placeholder="(optional)"
                      disabled={createMutation.isPending}
                    />
                  </FormField>
                  <FormField label="Scorecard delivery">
                    <Checkbox
                      checked={textScorecard}
                      onChange={({ detail }) => setTextScorecard(detail.checked)}
                      disabled={createMutation.isPending}
                    >
                      Text me the scorecard
                    </Checkbox>
                  </FormField>
                  <FormField label="Title">
                    <Input
                      value={title}
                      onChange={({ detail }) => setTitle(detail.value)}
                      placeholder="(optional)"
                      disabled={createMutation.isPending}
                    />
                  </FormField>
                </Grid>
              </ExpandableSection>
              <Box float="right">
                <Button
                  variant="primary"
                  iconName="add-plus"
                  onClick={handleCreate}
                  loading={createMutation.isPending}
                  disabled={selectedItems.length === 0}
                >
                  {selectedItems.length > 1
                    ? `Create Scorecards (${selectedItems.length})`
                    : 'Create Scorecard'}
                </Button>
              </Box>
              {createMutation.isError && (
                <LoadingOrError loading={createMutation.isPending} error={createMutation.error} />
              )}
              {blockedBusinesses.length > 0 && (
                <Alert type="error" statusIconAriaLabel="Error" header="Client is already using Real Time Feedback">
                  {blockedBusinesses.join(', ')}
                </Alert>
              )}
            </SpaceBetween>
          </Container>

          {/* In-progress reports */}
          {createdReports.length > 0 && (
            <div>
              {createdReports.map((report) => (
                <ReportProgress key={report.uuid} uuid={report.uuid} delivery={createdDelivery} />
              ))}
            </div>
          )}

          {/* Existing reports */}
          <div id="scorecard-history">
          <Container>
            <Table<DistributorReport>
              header={
                <Header
                  variant="h2"
                  counter={`(${total.toLocaleString()})`}
                  actions={
                    <Button
                      iconName="refresh"
                      variant="icon"
                      onClick={() => reportsQuery.refetch()}
                      loading={reportsQuery.isRefetching}
                    />
                  }
                  description={ownerFilter ? `Owner: ${ownerFilter}` : undefined}
                >
                  Scorecard History
                </Header>
              }
              filter={
                <SpaceBetween direction="vertical" size="xs">
                  <TextFilter
                    filteringText={searchText}
                    filteringPlaceholder="Search scorecards"
                    loading={reportsQuery.isFetching}
                    onChange={({ detail }) => {
                      setSearchText(detail.filteringText);
                      resetPage();
                    }}
                  />
                  <ExpandableSection headerText="Filters">
                    <SpaceBetween direction="vertical" size="s">
                      <Grid
                        gridDefinition={[
                          { colspan: { default: 12, s: 6, m: 3 } },
                          { colspan: { default: 12, s: 6, m: 3 } },
                          { colspan: { default: 12, s: 6, m: 3 } },
                          { colspan: { default: 12, s: 6, m: 3 } },
                        ]}
                      >
                        <FormField label="Owner path">
                          <Input
                            value={ownerFilter}
                            placeholder="example-distributor"
                            onChange={({ detail }) => {
                              setOwnerFilter(detail.value);
                              resetPage();
                            }}
                          />
                        </FormField>
                        <FormField label="Business type">
                          <Select
                            selectedOption={businessTypeFilter}
                            placeholder="All business types"
                            options={[
                              { label: 'All business types', value: '' },
                              ...BUSINESS_TYPES,
                            ]}
                            onChange={({ detail }) => {
                              setBusinessTypeFilter(
                                detail.selectedOption.value ? detail.selectedOption : null,
                              );
                              resetPage();
                            }}
                          />
                        </FormField>
                        <FormField label="Report type">
                          <Select
                            selectedOption={reportTypeFilter}
                            placeholder="All report types"
                            options={[
                              { label: 'All report types', value: '' },
                              { label: 'Internal', value: 'distributor_internal' },
                              { label: 'Public', value: 'distributor' },
                            ]}
                            onChange={({ detail }) => {
                              setReportTypeFilter(
                                detail.selectedOption.value ? detail.selectedOption : null,
                              );
                              resetPage();
                            }}
                          />
                        </FormField>
                        <FormField label="Sort">
                          <Select
                            selectedOption={sortOption}
                            options={[
                              { label: 'Newest first', value: 'report_at:desc' },
                              { label: 'Oldest first', value: 'report_at:asc' },
                              { label: 'Business A–Z', value: 'place:asc' },
                              { label: 'Owner A–Z', value: 'owner:asc' },
                              { label: 'Report type A–Z', value: 'report_type:asc' },
                            ]}
                            onChange={({ detail }) => {
                              setSortOption(detail.selectedOption);
                              resetPage();
                            }}
                          />
                        </FormField>
                      </Grid>
                      <Grid
                        gridDefinition={[
                          { colspan: { default: 12, s: 6, m: 3 } },
                          { colspan: { default: 12, s: 6, m: 3 } },
                        ]}
                      >
                        <FormField label="Run on or after">
                          <DatePicker
                            value={reportAtFrom}
                            placeholder="YYYY/MM/DD"
                            onChange={({ detail }) => {
                              setReportAtFrom(detail.value);
                              resetPage();
                            }}
                          />
                        </FormField>
                        <FormField label="Run on or before">
                          <DatePicker
                            value={reportAtTo}
                            placeholder="YYYY/MM/DD"
                            onChange={({ detail }) => {
                              setReportAtTo(detail.value);
                              resetPage();
                            }}
                          />
                        </FormField>
                      </Grid>
                    </SpaceBetween>
                  </ExpandableSection>
                </SpaceBetween>
              }
              items={reportItems}
              loading={reportsQuery.isLoading}
              loadingText="Loading scorecard history"
              columnDefinitions={compactHistory ? compactColumns : desktopColumns}
              pagination={
                <Pagination
                  currentPageIndex={Math.min(page, pagesCount)}
                  pagesCount={pagesCount}
                  disabled={reportsQuery.isFetching}
                  onChange={({ detail }) => setPage(detail.currentPageIndex)}
                />
              }
              empty={
                <Box textAlign="center" color="inherit" padding="l">
                  {reportsQuery.isError ? (
                    <LoadingOrError
                      loading={false}
                      error={reportsQuery.error}
                      retry={() => reportsQuery.refetch()}
                    />
                  ) : hasFilters ? (
                    <SpaceBetween size="xxs">
                      <b>No matching scorecards</b>
                      <Box color="inherit">Clear or change the filters to see more history.</Box>
                    </SpaceBetween>
                  ) : (
                    <SpaceBetween size="xxs">
                      <b>No scorecards yet</b>
                      <Box color="inherit">Create one above to start the history.</Box>
                    </SpaceBetween>
                  )}
                </Box>
              }
              variant="embedded"
            />
          </Container>
          </div>
          <DistributorHierarchy
            onViewScorecards={(item) => {
              setOwnerFilter(item.path);
              resetPage();
              document.getElementById('scorecard-history')?.scrollIntoView({ block: 'start' });
            }}
          />
        </Grid>
      </ViewLayout>
    </>
  );
}
